/**
 * PRD.227 §11 — Compact adjustment summary for the Visit Billing panel.
 *
 * Sits as a third peer section next to GuidedInvoiceSummary ("Invoices")
 * and GuidedPaymentSummary ("Payments"). Shows the encounter-scoped
 * Debits / Credits / Net for billing_adjustments tied to any invoice
 * on this visit. All money math via Dinero.
 */

import { useEffect, useMemo, useState } from 'react';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import {
  qInvoicesByEncounter,
  type BillingAdjustmentRow,
  type InvoiceRow,
} from '@helios/data-access';
import { phpFromCents, subtractMoney, sumMoney } from '@helios/domain';
import { useFormatMoney } from '../../../hooks/useFormatMoney';

interface Props {
  patientId: string;
  encounterId: string;
}

export function GuidedAdjustmentSummary({ patientId, encounterId }: Props) {
  const { db } = usePowerSyncDb();
  const fmt = useFormatMoney();
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(
    qInvoicesByEncounter(patientId, encounterId),
  );

  // billing_adjustments has no direct encounter scope today, so we pull
  // adjustments tied to any of this encounter's invoices. Same pattern
  // as GuidedPaymentSummary uses for payments — one IN-clause fetch on
  // invoice_id, re-runs when the invoice set or db handle changes.
  const [adjustmentRows, setAdjustmentRows] = useState<BillingAdjustmentRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!db || invoiceRows.length === 0) {
      setAdjustmentRows([]);
      return;
    }
    const invoiceIds = invoiceRows.map((inv) => inv.id);
    const placeholders = invoiceIds.map(() => '?').join(',');
    db.getAll<BillingAdjustmentRow>(
      `SELECT * FROM billing_adjustments WHERE invoice_id IN (${placeholders})`,
      invoiceIds,
    )
      .then((rows) => {
        if (!cancelled) setAdjustmentRows(rows);
      })
      .catch(() => {
        if (!cancelled) setAdjustmentRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [db, invoiceRows, encounterId]);

  const { debit, credit, net, hasAny } = useMemo(() => {
    const debits = adjustmentRows
      .filter((r) => r.adjustment_type === 'debit')
      .map((r) => phpFromCents(r.amount_cents ?? 0));
    const credits = adjustmentRows
      .filter((r) => r.adjustment_type === 'credit')
      .map((r) => phpFromCents(r.amount_cents ?? 0));
    const debitTotal = sumMoney(debits);
    const creditTotal = sumMoney(credits);
    const netVal = subtractMoney(debitTotal, creditTotal);
    return {
      debit: debitTotal,
      credit: creditTotal,
      net: netVal,
      hasAny: adjustmentRows.length > 0,
    };
  }, [adjustmentRows]);

  if (invoiceRows.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--sp-5) var(--sp-6)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          fontStyle: 'italic',
        }}
      >
        No adjustments for this visit
      </div>
    );
  }

  if (!hasAny) {
    return (
      <div
        style={{
          padding: 'var(--sp-5) var(--sp-6)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          fontStyle: 'italic',
        }}
      >
        No adjustments recorded for this visit
      </div>
    );
  }

  // Render +/− prefix on Net to make the direction unambiguous.
  const netLabel = net.isZero()
    ? fmt(net)
    : net.isPositive()
      ? `+${fmt(net)}`
      : `−${fmt(phpFromCents(Math.abs(net.getAmount())))}`;

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-6)',
        display: 'flex',
        gap: 'var(--sp-5)',
        flexWrap: 'wrap',
      }}
    >
      <SummaryItem label="Debits" value={fmt(debit)} color="var(--color-warning-text)" />
      <SummaryItem label="Credits" value={fmt(credit)} color="var(--color-success-text)" />
      <SummaryItem label="Net" value={netLabel} />
    </div>
  );
}

function SummaryItem({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: color ?? 'var(--text)' }}
      >
        {value}
      </span>
    </div>
  );
}
